import React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { SidePanelSheet } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    clipboard,
    DialogRoot,
    LabeledContainerSkeleton,
    RevealableToolbar,
    RevealableToolbarItem,
    RevealableToolbarRoot,
    Separator,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import {
    CHANGE_REQUEST_STATUS_ICONS,
    ChangeRequestCreatedBySection,
    ChangeRequestDescriptionField,
    ChangeRequestMergeRulesStatus,
    ChangeRequestParticipantsList,
    ChangeRequestReviewersList,
    ChangeRequestReviewRequestDialog,
    ChangeRequestStatusBadge,
    ChangeRequestSubject,
} from '../../../../Components';
import { useNavigation } from '../../../../navigation';

/**
 * Overview panel for change requests.
 */
export function ChangeRequestSidePanel(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
}): React.ReactElement | null {
    const { changeRequest, space } = props;

    const requestReviewDialogTrigger = useDialogTriggerState();
    const { navigate } = useNavigation();

    return (
        <>
            <SidePanelSheet
                title="Overview"
                onClose={() => {
                    analytics.logEvent(
                        analytics.createChangeRequestEvent(space, changeRequest.id, {
                            type: 'change_request_details_panel_close',
                        })
                    );
                    navigate({
                        screen: {
                            sideSheet: undefined,
                        },
                    });
                }}
            >
                <div className="flex flex-1 flex-col gap-3 p-6">
                    <div className="flex flex-col gap-1">
                        <div className="-mx-2 flex-auto flex-shrink">
                            <ChangeRequestSubject
                                space={space}
                                changeRequest={changeRequest}
                                size="medium"
                                editable
                            />
                        </div>
                        <div className="flex flex-row items-center gap-2">
                            <RevealableToolbarRoot>
                                <ChangeRequestStatusBadge
                                    icon={CHANGE_REQUEST_STATUS_ICONS[changeRequest.status]}
                                    changeRequest={changeRequest}
                                    withIcon={false}
                                />
                                <div className="font-normal text-muted text-ui-base">
                                    #{changeRequest.number}
                                </div>
                                <RevealableToolbar aria-label="Copy Actions">
                                    <RevealableToolbarItem>
                                        <Button
                                            kind="blank"
                                            size="small"
                                            iconOnly
                                            label="Copy change request URL"
                                            leadingIcon={Icon.Copy}
                                            onPress={() => {
                                                clipboard.writeText(changeRequest.urls.app);
                                            }}
                                        />
                                    </RevealableToolbarItem>
                                </RevealableToolbar>
                            </RevealableToolbarRoot>
                        </div>
                    </div>
                    <ChangeRequestDescriptionField changeRequest={changeRequest} />
                    <Separator />
                    <React.Suspense fallback={<LabeledContainerSkeleton />}>
                        <ChangeRequestMergeRulesStatus
                            space={space}
                            changeRequest={changeRequest}
                        />
                    </React.Suspense>
                    <Separator />
                    <ChangeRequestCreatedBySection changeRequest={changeRequest} />
                    <Separator />
                    <ChangeRequestParticipantsList changeRequest={changeRequest} />
                    <Separator />

                    <ChangeRequestReviewersList changeRequest={changeRequest} space={space} />
                </div>
            </SidePanelSheet>
            <DialogRoot {...requestReviewDialogTrigger.rootProps}>
                <ChangeRequestReviewRequestDialog changeRequest={changeRequest} space={space} />
            </DialogRoot>
        </>
    );
}
